<template>
  <v-container>
    <base-snackbar :errors.sync="verifyErrors" />
    <v-row>
      <v-col class="pa-1" cols="12" lg="4">
        <v-card class="ma-0">
          <v-toolbar color="primary" flat dark class="mb-4">
            <v-card-title> Add Transaction </v-card-title>
          </v-toolbar>
          <v-row class="mx-2">
            <v-col cols="12">
              <v-text-field
                label="Receipt No"
                hide-details="auto"
                dense
                outlined
                v-model="receiptNo"
                :error-messages="verifyErrors.receiptNo"
              />
            </v-col>

            <v-col cols="12">
              <v-autocomplete
                label="Stock Code"
                :items="stockList.list"
                :search-input.sync="stockSearchInput"
                :loading="stockList.loading"
                item-value="stockCode"
                no-filter
                v-model="selectedStock"
                :error-messages="verifyErrors.stockId"
                return-object
                hide-details="auto"
                dense
                outlined
              >
                <template v-slot:selection="{ item }">
                  <span>{{ item.supplierId }}-{{ item.stockCode }}</span>
                  <span class="blue-grey--text mx-1 text-caption">{{
                    item.productName
                  }}</span>
                </template>
                <template v-slot:item="{ item }">
                  <div
                    class="d-flex w-100"
                    :style="{ justifyContent: 'space-between', width: '100%' }"
                  >
                    <div class="d-flex">
                      <v-icon v-if="item.promotion"
                        >mdi-information-variant</v-icon
                      >
                      <v-icon v-if="item.wholeSale?.conditions?.length"
                        >mdi-package</v-icon
                      >
                      <span class="blue--text mx-1 text-left"
                        >{{ item.supplierId }}-{{ item.stockCode }}</span
                      >
                    </div>
                    <span class="mx-1"></span>
                    <small>{{ item.productName }}</small>
                  </div>
                </template>
              </v-autocomplete>
            </v-col>

            <!-- WHOLESALE INFO (now emits @apply) -->
            <v-col v-if="selectedStock?.wholeSale?.conditions?.length">
              <whole-sale-info
                :conditions="selectedStock.wholeSale.conditions"
                :quantity="quantity"
                :retail="retail"
              />
            </v-col>

            <v-col v-if="!!selectedStock?.promotion">
              <promo-info
                :promo-item="promoItem"
                :supplier-product="selectedStock"
                :allow-promo.sync="allowPromo"
                @update:allowPromo="allowPromo = $event"
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                hide-details="auto"
                dense
                outlined
                label="Senior ID"
                v-model="refNumber"
                :error-messages="verifyErrors.refNumber"
              />
            </v-col>
            <v-col cols="12">
              <v-combobox
                hide-details="auto"
                dense
                outlined
                v-model="consumer"
                :items="filteredConsumers"
                :search-input.sync="searchConsumer"
                hide-selected
                label="Consumer"
                :error-messages="verifyErrors.consumer"
                no-filter
                return-object
                @keyup.enter="onConsumerComboboxEnter"
              >
                <template #selection="{ item }">
                  <small>({{ item.controlNumber }})</small>&nbsp;<span>{{
                    item.name
                  }}</span>
                </template>
                <template #item="{ item }">
                  <small>({{ item.controlNumber }})</small>&nbsp;<span>{{
                    item.name
                  }}</span>
                </template>
                <template v-slot:no-data>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>
                        No results matching
                        <strong>{{ searchConsumer }}</strong>
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-combobox>
            </v-col>

            <v-col cols="12">
              <v-text-field
                v-if="authPermissions.has('sales:date_edit')"
                hide-details="auto"
                dense
                outlined
                label="Transaction Date"
                type="date"
                :disabled="!supplierId"
                v-model="dateTransaction"
                :error-messages="verifyErrors.dateTransaction"
              />
              <v-text-field
                v-else
                hide-details="auto"
                dense
                outlined
                label="Transaction Date"
                type="date"
                readonly
                :disabled="!supplierId"
                :value="dateTransaction"
                :error-messages="verifyErrors.dateTransaction"
              />
            </v-col>

            <v-col cols="12">
              <v-text-field
                v-if="authPermissions.has('sales:unsaved_price_edit')"
                hide-details="auto"
                dense
                outlined
                label="SRP"
                prefix="₱"
                type="number"
                v-model.number="retail"
                :disabled="!stockId"
                :error-messages="verifyErrors.retail"
              />
              <v-text-field
                v-else
                hide-details="auto"
                dense
                outlined
                label="SRP"
                prefix="₱"
                type="number"
                :value="retail"
                readonly
                :error-messages="verifyErrors.retail"
              />
            </v-col>

            <v-col cols="3">
              <v-text-field
                hide-details="auto"
                dense
                outlined
                label="Discount/Item"
                suffix="%"
                type="number"
                min="0"
                max="100"
                @keyup="onItemDiscountPercentageChange"
                v-model.number="itemDiscountPercentage"
                :disabled="
                  !stockId ||
                  ((!!selectedStock?.promotion ||
                    !!selectedStock?.wholeSale?.length) &&
                    allowPromo)
                "
                :error-messages="verifyErrors.retail"
              />
            </v-col>

            <v-col cols="9">
              <v-text-field
                hide-details="auto"
                dense
                outlined
                label="Total Discounted Amnt"
                prefix="₱"
                type="number"
                v-model.number="discount"
                :disabled="
                  !stockId ||
                  ((!!selectedStock?.promotion ||
                    !!selectedStock?.wholeSale?.length) &&
                    allowPromo)
                "
                :error-messages="verifyErrors.retail"
              />
            </v-col>

            <v-col cols="12">
              <v-text-field
                hide-details="auto"
                dense
                outlined
                type="number"
                :rules="quantityRules"
                v-model.number="quantity"
                @keyup="onQuantityChange"
                :suffix="unit"
                :placeholder="quantityLeft < 1 ? 'No Stock Available' : ''"
                :disabled="!stockId"
                :error-messages="verifyErrors.quantity"
              >
                <template v-slot:label>
                  <span>Quantity</span>
                  <span class="mx-1" v-if="quantityLeft > 0"
                    >({{ quantityLeft }})</span
                  >
                </template>
              </v-text-field>
            </v-col>

            <v-col cols="12">
              <v-textarea
                hide-details="auto"
                dense
                outlined
                label="Notes"
                auto-grow
                no-resize
                v-model="notes"
                rows="1"
                :error-messages="verifyErrors.notes"
              />
            </v-col>

            <v-col cols="12">
              <v-btn
                color="green"
                block
                @click="addTransaction"
                :loading="verifying"
              >
                Add Transaction
              </v-btn>
            </v-col>
          </v-row>
        </v-card>
      </v-col>

      <v-col class="pa-1" cols="12" lg="8">
        <v-card class="ma-0">
          <v-toolbar color="primary" dark>
            <span>Unsaved Transactions</span>
            <v-spacer></v-spacer>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attr }">
                <v-btn
                  class="ma-0"
                  text
                  v-bind="attr"
                  v-on="on"
                  :disabled="!tempList.length"
                  @click="upload"
                >
                  <v-icon>mdi-upload</v-icon>
                </v-btn>
              </template>
              <span>Upload Data</span>
            </v-tooltip>
          </v-toolbar>

          <v-alert
            class="py-0 text-uppercase"
            dense
            type="error"
            outlined
            text
            dismissible
            transition="slide-x-transition"
            v-for="(item, index) in splrPdUploadErrors"
            :key="index"
          >
            {{ item[0] }}
          </v-alert>

          <v-card-text style="overflow: auto">
            <v-responsive min-width="1200">
              <v-data-table
                class="elevation-0"
                :headers="headers"
                :items="tempList"
                :loading="splrPdUploading"
                hide-default-footer
                disable-pagination
              >
                <template v-slot:body.append>
                  <tr>
                    <td colspan="7" align="right"><strong>Total</strong></td>
                    <td align="right">{{ totalAmount.phpFormat() }}</td>
                  </tr>
                </template>

                <template v-slot:item.config="{ item }">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        class="mx-2"
                        text
                        dark
                        x-small
                        v-bind="attrs"
                        v-on="on"
                        color="red"
                        @click="deleteTempUnit(item.tempId)"
                      >
                        <v-icon dark>mdi-delete</v-icon>
                      </v-btn>
                    </template>
                  </v-tooltip>
                </template>

                <template v-slot:item.discount="{ value }">
                  {{ (value || 0).phpFormat() }}
                </template>
                <template v-slot:item.retail="{ value }">
                  {{ (value || 0).phpFormat() }}
                </template>
                <template v-slot:item.totalAmount="{ value }">
                  {{ (value || 0).phpFormat() }}
                </template>
                <template v-slot:item.dateSold="{ value }">
                  {{ (value || '').dateFormat() }}
                </template>
              </v-data-table>
            </v-responsive>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { moneyMixin } from '@/views/dashboard/mixins/helper.mixins'
import { mapState, mapActions } from 'vuex'
import indexKeyGenerator from '@/extra-functions/index-key-generator.extra'
import consumerRepository from '@/repositories/consumer.repository'
import { errorHandler } from '@/extra-functions/store.extra'
import appAxios, { authAxios } from '@/extra-functions/axios.extra'
import {
  ErrorPopup,
  initBarcodeEventHandler,
  LoadingPopup,
  SuccessPopup,
  roundOff,
  useWatchEffect,
  useDebounce,
} from '@/lib/utils'
import PromotionUtil from '../../../../../lib/promotion.util'
import WholeSaleUtil from '../../../../../lib/whole-sale.util'
import DiscountUtil from '../../../../../lib/discount.util'
import { AxiosError } from 'axios'

const [watchEffect, clearWatchEffect] = useWatchEffect()
const barcodeEventHandler = initBarcodeEventHandler()
const debounce = useDebounce()

function currentDate() {
  const date = new Date()
  const year = date.getFullYear()
  const month = `${date.getMonth() + 1}`.padStart(2, '0')
  const day = `${date.getDate()}`.padStart(2, '0')
  return `${year}-${month}-${day}`
}

const inputVars = () => {
  return {
    receiptNo: null,
    supplierId: null,
    stockSearchInput: null,
    retail: null,
    stockId: '',
    unit: '',
    unitSearchInput: null,
    quantity: null,
    discount: null,
    dateTransaction: currentDate(),
    notes: null,
    selectedStock: null,
    allowPromo: false,
    refNumber: null, // 👈 added here
  }
}
function consumerVars() {
  return {
    consumer: null,
    consumers: [],
    searchConsumer: null,
    searchingConsumer: false,
    tempConsumers: [],
  }
}

function tableVars() {
  return {
    headers: [
      { text: 'Config', value: 'config' },
      { text: 'Receipt No.', value: 'receiptNo', sortable: false },
      { text: 'Consumer', value: 'consumerName', sortable: false },
      { text: 'Product Name', value: 'productName' },
      { Next: 'Brand', value: 'brand_name' },
      { text: 'Discount', value: 'discount' },
      { text: 'SRP', value: 'retail' },
      { text: 'Quantity', value: 'quantity' },
      { text: 'Total Amount', value: 'totalAmount' },
      { text: 'Date', value: 'dateSold' },
    ],
    promoItem: null,
    page: 1,
    itemsPerPage: 10,
  }
}

export default {
  name: 'createSaleInventories',
  components: {
    wholeSaleInfo: () => import('../WholeSaleInfo.vue'),
    promoInfo: () => import('../PromoInfo.vue'),
  },
  data() {
    return {
      ...inputVars(),
      ...consumerVars(),
      ...tableVars(),
      stockList: {
        data: [],
        loading: false,
      },
      itemDiscountPercentage: null,
      receiptNoQuery: { getting: false },
      quantityRules: [
        (val) => {
          const ok = this.quantityLeft >= val
          return ok || `Only has ${this.quantityLeft} ${this.unit} in stock`
        },
      ],
      seniorDiscountRate: 0.2,
      discountRates: {
        senior: 0,
        promo: 0,
        wholeSale: 0,
      },
      tempUnits: [],
      panel: true,
      verifyErrors: {},
    }
  },
  created() {
    this.onInit()
  },
  mounted() {
    window.addEventListener('keypress', this.handleBarcodeEvent)
  },
  beforeDestroy() {
    window.removeEventListener('keypress', this.handleBarcodeEvent)
    clearWatchEffect()
  },
  computed: {
    ...mapState({
      // verifyErrors: (state) => state.saleInventory.verifyErrors,
      verifying: (state) => state.saleInventory.verifying,
      tempList: (state) => state.saleInventory.tempList,
      unitsUpdated: (state) => state.unit.channelUpdate,
      units: (state) => state.unit.allList,
      uploading: (state) => state.saleInventory.storing,
      uploadSuccess: (state) => state.saleInventory.storeSuccess,
      uploadErrors: (state) => state.saleInventory.storeErrors,
      saleInventoryUpdated: (state) => state.saleInventory.channelUpdate,
      storeId: (state) => state.settings.storeId,
      authToken: (state) => state.auth.token,
      authPermissions: (state) => state.auth.userPermissions,
    }),
    cost() {
      return this.selectedStock?.cost || null
    },
    totalAmount() {
      return this.tempList.reduce((total, item) => total + item.totalAmount, 0)
    },
    quantityLeft() {
      if (!this.selectedStock) return 0
      const stockId = this.stockId
      const supplierId = this.supplierId
      const qRemaining = !Object.entries(this.selectedStock).length
        ? 0
        : this.selectedStock.qRemaining
      const quantity = this.tempList
        .filter((e) => stockId === e.stockId && supplierId === e.supplierId)
        .reduce((a, b) => a + parseFloat(b.quantity), 0)
      return qRemaining - quantity
    },
    filteredConsumers() {
      const tempConsumers = this.tempConsumers.filter((v) =>
        new RegExp(this.searchConsumer).test(`${v.controlNumber}${v.name}`),
      )
      return this.consumers.concat(tempConsumers)
    },
  },
  watch: {
    searchConsumer() {
      this.fetchConsumers()
    },
    unit(val) {
      const newUnit = this.units.find((e) => e.unit === val)
      if (newUnit === -1 || newUnit === undefined) {
        this.tempUnits.push({
          unit: val,
          description: 'Not in database',
          temp: true,
        })
      }
    },
    stockSearchInput() {
      this.stockList.loading = true
      debounce(this.fetchStocks)
    },
    selectedStock(sp) {
      this.allowPromo = !!sp?.promotion
      if (!sp) {
        this.stockId = null
        this.supplierId = null
        this.retail = null
        this.unit = null
        this.discount = 0
        this.itemDiscountPercentage = null
        return
      }
      this.stockId = sp.stockId
      this.supplierId = sp.supplierId
      this.retail = sp.retail
      this.unit = sp.unit
      this.$nextTick(() => {
        this.quantity = null
        return
      })
      // ensure a sensible default qty so tiers can trigger
      this.$nextTick(() => {
        this.applyPromoAndDiscounts()
      })
    },
    // quantity() {
    //   this.$nextTick(() => this.applyPromoAndDiscounts())
    // },
    retail() {
      this.$nextTick(() => this.applyPromoAndDiscounts())
    },
    unitsUpdated() {
      this.fetchUnitsAll()
    },
    supplierUpdated() {
      this.fetchSuppliers({ index: 0, count: 10, search: '' })
    },
    saleInventoryUpdated() {
      this.getLatestReceiptNo()
    },
    refNumber() {
      this.$nextTick(() => this.applyPromoAndDiscounts())
    },
    consumer(v) {
      if (typeof v === 'string') {
        const consumer = {
          id: null,
          name: v,
          controlNumber: 'nw-' + this.tempConsumers.length,
        }
        this.tempConsumers.push(consumer)
        this.consumer = consumer
      }
    },
  },
  methods: {
    ...mapActions({
      fetchSupplierProducts: 'selectedStock/fetch',
      fetchUnitsAll: 'unit/fetchAll',
      checkErrors: 'saleInventory/checkErrors',
      addTempItem: 'saleInventory/addTempItemV2',
      updateTempItem: 'saleInventory/updateTempItem',
      removeTempItem: 'saleInventory/removeTempItem',
      clearTempList: 'saleInventory/clearTempList',
    }),
    onInit() {
      this.fetchSeniorDiscountRate()
      this.fetchConsumers()
      this.dateTransaction = currentDate()
      this.fetchUnitsAll()
      this.fetchStocks()
      this.getLatestReceiptNo()
    },
    fetchSeniorDiscountRate() {
      watchEffect(async (onCleanup) => {
        try {
          const controller = new AbortController()
          onCleanup(() => controller.abort())
          const axios = authAxios()
          const { data } = await axios.get(
            '/v2/identity-card-types/senior-discount',
            { signal: controller.signal },
          )
          this.seniorDiscountRate = data?.discountRate ?? 0.2
        } catch (e) {
          console.error(e)
          ErrorPopup.fire(
            'Error',
            'Something went wrong while fetching senior discount rate retrying in 3 sec',
          )
          setTimeout(this.fetchSeniorDiscountRate, 3000)
        }
      }, 'SaleInventoryV2Create_fetchSeniorDiscountRate')
    },
    async fetchStockByBarcode(barcode) {
      try {
        if (!barcode) return
        LoadingPopup.fire('Loading', 'Getting Product Data...')
        const { data } = await appAxios(this.authToken).get(
          '/v2/stock-suppliers/dropdown',
          {
            params: {
              barcode,
              limit: 1,
            },
          },
        )
        if (!data?.data?.length) throw new Error('no stock found')
        const stock = data.data[0]
        this.stockList.list.push(stock)
        LoadingPopup.close()
        return data.data.length > 0
      } catch (e) {
        LoadingPopup.close()
        console.error(e)
        if (e instanceof Error && e.message === 'no stock found') {
          ErrorPopup.fire('Error', e.message + ` on barcode: [${barcode}]`)
          return false
        }
        ErrorPopup.fire('Error', 'Oops something went wrong!!!')
        return false
      }
    },
    handleBarcodeEvent(e) {
      const checkBarcode = (barcode) => {
        const tempListIndex = this.tempList.findIndex(
          (item) => item.barcode === barcode,
        )
        const isInTempList = tempListIndex > -1
        if (isInTempList) {
          const item = this.tempList[tempListIndex]
          const quantity = item.quantity + 1
          this.updateTempItem([
            tempListIndex,
            {
              quantity,
              totalAmount: this.computeAmount(
                item?.retail,
                quantity,
                item?.discount,
              ),
            },
          ])
          return true
        }
        const productListIndex = this.stockList.list.findIndex(
          (item) => item.barcode === barcode,
        )
        const isInProductList = productListIndex > -1

        if (isInProductList) {
          const item = this.stockList.list[productListIndex]
          this.selectedStock = item
          this.$nextTick(() => {
            this.quantity = this.quantity || 1
            this.addTransaction()
          })
          return true
        }
        return false
      }
      barcodeEventHandler(e.key, async (barcode) => {
        const isFound = checkBarcode(barcode)
        if (isFound) return
        const isStockFound = await this.fetchStockByBarcode(barcode)
        if (!isStockFound) return
        checkBarcode(barcode)
      })
    },
    async upload() {
      try {
        LoadingPopup.fire('Uploading', 'storing data to server')
        this.splrPdUploadErrors
        const data = this.tempList.map((e) => {
          return {
            receiptNo: e.receiptNo,
            storeId: e.storeId,
            consumerName: e.consumerName,
            stockId: e.stockId,
            consumer: e.consumer,
            retail: e.retail,
            supplierId: e.supplierId,
            unit: e.unit,
            quantity: e.quantity,
            dateSold: e.dateSold,
            discount: e.discount,
            notes: e.notes,
            cost: e.cost,
            allowPromo: e.allowPromo,
            consumerId: e.consumerId,
            refNumber: e.refNumber,
            consumerControlNumber: e.consumerControlNumber,
          }
        })
        const axios = authAxios()
        const response = await axios.post('/v2/sale-inventories', { data })
        LoadingPopup.close()
        SuccessPopup.fire(
          'Success',
          response.data?.message || 'Sales successfully stored',
        )
        this.$emit('upload-success')
        this.clearInputs()
        this.clearTempList()
        this.onInit()
      } catch (e) {
        LoadingPopup.close()
        this.splrPdUploadErrors = errorHandler(e)
      }
    },
    clearInputs() {
      const inputV = inputVars()
      Object.keys(inputV).forEach((e) => {
        this[e] = inputV[e]
      })
    },
    fetchStocks() {
      watchEffect(async (onCleanup) => {
        try {
          this.stockList.loading = true
          const controller = new AbortController()
          onCleanup(() => {
            controller.abort()
          })
          const axios = authAxios()
          const { data } = await axios.get('/v2/stock-suppliers/dropdown', {
            params: { search: this.stockSearchInput },
            signal: controller.signal,
          })
          this.stockList.list = data.data
        } catch (e) {
          ErrorPopup.fire('Error', 'Failed to get stocks retrying in 3 secs')
          setTimeout(this.fetchStocks, 3000)
        } finally {
          this.stockList.loading = false
        }
      }, 'fetchStocks')
    },
    addTransaction() {
      watchEffect(async () => {
        try {
          this.verifyErrors = {}
          LoadingPopup.fire('Checking', 'Checking Input Errors')
          const selectedStock = this.selectedStock
          const data = {
            receiptNo: this.receiptNo,
            storeId: this.storeId,
            barcode: this.selectedStock?.barcode || null,
            consumerName: this.consumer?.name || '',
            stockId: this.stockId,
            retail: this.retail,
            supplierId: this.supplierId,
            unit: this.unit,
            discount: this.discount,
            quantity: this.quantity,
            dateSold: this.dateTransaction,
            notes: this.notes,
            cost: this.cost,
            allowPromo: this.allowPromo,
            consumerId: this.consumer?.id ?? null,
            refNumber: this.refNumber,
          }
          const axios = authAxios()
          const ret = await axios.post(
            '/v2/sale-inventories/check-errors',
            data,
          )
          this.quantity = null
          // self.createNewConsumer(data)
          this.addTempItem(
            Object.assign(
              data,
              {
                stockCode: selectedStock.stockCode,
                brand_name: selectedStock.brand_name,
                productName: selectedStock.productName,
                totalAmount: this.computeAmount(
                  data?.retail,
                  data?.quantity,
                  data?.discount,
                ),
                consumerControlNumber: this.consumer?.controlNumber ?? null,
              },
              ret.data,
            ),
          )
          this.selectedStock = null
        } catch (e) {
          console.error('AddTransaction Error', e)
          if (e instanceof AxiosError) {
            this.verifyErrors = e.response?.data?.errors || {}
          }
        } finally {
          LoadingPopup.close()
        }
      }, 'SaleInventoryV2Create_addTransaction')
    },
    dateFormat(ndate) {
      const date = new Date(ndate)
      const year = date.getFullYear()
      const month = `${date.getMonth() + 1}`.padStart(2, '0')
      const day = `${date.getDate()}`.padStart(2, '0')
      return `${month}/${day}/${year}`
    },
    deleteTempUnit(tempId) {
      this.removeTempItem(tempId)
    },
    initFetchSupplierProducts() {
      this.fetchSupplierProducts(this.supplierId)
    },
    computeAmount(retail = 0, quantity = 0, discount = null) {
      if (!discount) return retail * quantity
      return retail * quantity - discount
    },
    fetchConsumers() {
      watchEffect(async (callback) => {
        try {
          const controller = new AbortController()
          callback(() => {
            controller.abort()
          })
          this.searchingConsumer = true
          const axios = authAxios()
          const { data } = await axios.get('/v2/consumers/dropdown', {
            params: {
              search: this.searchConsumer,
            },
            signal: controller.signal,
          })
          this.consumers = data?.data || []
        } catch (e) {
          console.error(e.message)
          ErrorPopup.fire(
            'Error',
            'Something went wrong while retrieving consumer list retrying in 3 sec...',
          )
          setTimeout(this.fetchConsumers, 3000)
        } finally {
          this.searchingConsumer = false
        }
      }, 'fetchConsumers')
    },
    createNewConsumer(data) {
      if (!data.consumerName) return
      var errorCode = 0
      do {
        consumerRepository
          .new({
            id: indexKeyGenerator(),
            name: data.consumerName,
            storeId: this.storeId,
          })
          .then(() => {})
          .catch(({ response }) => {
            errorCode = response.status
          })
      } while (errorCode === 422)
    },
    getLatestReceiptNo() {
      watchEffect(async (onCleanup) => {
        try {
          const controller = new AbortController()
          onCleanup(() => {
            controller.abort()
          })
          const axios = appAxios(this.authToken)
          if (this.receiptNoQuery.getting) return
          this.receiptNoQuery = { ...this.receiptNoQuery, getting: true }
          const { data } = await axios.get(
            'sale-inventories/latest-receipt-no',
            { signal: controller.signal },
          )
          this.receiptNo = data.toString()
        } catch (e) {
          console.error(e)
          setTimeout(() => this.getLatestReceiptNo, 3000)
        } finally {
          this.receiptNoQuery = { ...this.receiptNoQuery, getting: false }
        }
      }, 'getLatestReceiptNo')
    },
    applyPromoAndDiscounts() {
      if (!this.selectedStock.stockId) {
        this.promoItem = null
        return
      }
      if (
        !this.selectedStock?.promotion?.promoItem &&
        !this.selectedStock?.wholeSale
      ) {
        this.promoItem = null
        return
      }
      console.log('applying promo and discounts')
      const promotionUtil = new PromotionUtil(
        this.selectedStock.stockId,
        this.selectedStock?.promotion?.promoItem,
      )
      promotionUtil.setQuantity(this.quantity)
      this.promoItem = promotionUtil.getPromoItem()

      const wholeSaleUtil = new WholeSaleUtil(
        this.selectedStock?.wholeSale?.conditions || [],
      )
      wholeSaleUtil.setQuantity(this.quantity)
      wholeSaleUtil.setRetail(this.retail)
      const seniorDiscountRate =
        Number(this.refNumber && this.seniorDiscountRate) || 0
      const discountUtil = new DiscountUtil(
        promotionUtil.getDiscount(),
        wholeSaleUtil.getDiscount(),
        seniorDiscountRate,
      )
      discountUtil.setQuantity(this.quantity)
      discountUtil.setRetail(this.retail)
      this.itemDiscountPercentage =
        Math.round(discountUtil.getDiscountRate() * 1000) / 10
      this.discount = Math.round(discountUtil.getDiscount() * 100) / 100
    },
    /**
     * Description
     * @param {KeyboardEvent} [e]
     * @returns {any}
     */
    onItemDiscountPercentageChange(e) {
      if (this.allowPromo) return
      if (e instanceof KeyboardEvent) {
        const val = e.target?.value || 0
        this.discount = roundOff((val / 100) * this.retail * this.quantity)
        return
      }
      this.discount = roundOff(
        (this.itemDiscountPercentage / 100) * this.retail * this.quantity,
      )
    },
    onQuantityChange() {
      if (!this.allowPromo) {
        this.onItemDiscountPercentageChange()
        return
      }
      this.applyPromoAndDiscounts()
    },
  },
  mixins: [moneyMixin],
}
</script>

<style lang="scss" scoped>
.truncate {
  max-width: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
